import type { ElementContent, RootContent } from 'hast'
import { common, createLowlight } from 'lowlight'
import type { TextFilePreviewSyntaxLanguage } from '@awork/_shared/functions/text-file-preview'

const lowlight = createLowlight(common)
const MAX_CACHE_ENTRIES = 20
const HIGHLIGHT_CLASS_PATTERN = /^(?:hljs-[a-z0-9_-]+|class_|function_|inherited__|language_)$/
const highlightedHtmlCache = new Map<string, string | null>()

/**
 * Highlights source text and serializes only safe Highlight.js spans.
 * @param {string} content Original source content.
 * @param {TextFilePreviewSyntaxLanguage} language Explicit grammar name.
 * @param {number} blobSizeBytes Loaded Blob size used by the cache key.
 * @returns {string | null} Safe HTML, or null when highlighting fails.
 */
export function getTextFilePreviewHighlightedHtml(
  content: string,
  language: TextFilePreviewSyntaxLanguage,
  blobSizeBytes: number
): string | null {
  const cacheKey = `${language}\u0000${blobSizeBytes}\u0000${content}`
  const cachedHtml = highlightedHtmlCache.get(cacheKey)

  if (cachedHtml !== undefined || highlightedHtmlCache.has(cacheKey)) {
    return cachedHtml ?? null
  }

  let highlightedHtml: string | null

  try {
    highlightedHtml = serializeNodes(lowlight.highlight(language, content).children)
  } catch {
    highlightedHtml = null
  }

  highlightedHtmlCache.set(cacheKey, highlightedHtml)

  if (highlightedHtmlCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = highlightedHtmlCache.keys().next().value

    if (oldestKey !== undefined) {
      highlightedHtmlCache.delete(oldestKey)
    }
  }

  return highlightedHtml
}

/**
 * Serializes a list of HAST nodes through the restricted preview format.
 * @param {readonly RootContent[] | readonly ElementContent[]} nodes HAST nodes to serialize.
 * @returns {string} Escaped HTML containing only allowed spans.
 */
function serializeNodes(nodes: readonly RootContent[] | readonly ElementContent[]): string {
  return nodes.map(node => serializeNode(node)).join('')
}

/**
 * Serializes one supported HAST node and ignores unsupported markup.
 * @param {RootContent | ElementContent} node HAST node to serialize.
 * @returns {string} Restricted HTML fragment.
 */
function serializeNode(node: RootContent | ElementContent): string {
  if (node.type === 'text') {
    return escapeHtml(node.value)
  }

  if (node.type !== 'element') {
    return ''
  }

  const children = serializeNodes(node.children)

  if (node.tagName !== 'span') {
    return children
  }

  const classNames = Array.isArray(node.properties.className)
    ? node.properties.className.filter(
        (className): className is string => typeof className === 'string' && HIGHLIGHT_CLASS_PATTERN.test(className)
      )
    : []
  const classAttribute = classNames.length ? ` class="${classNames.join(' ')}"` : ''

  return `<span${classAttribute}>${children}</span>`
}

/**
 * Escapes source text for safe insertion into an HTML fragment.
 * @param {string} value Source text value.
 * @returns {string} Escaped text.
 */
function escapeHtml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
}
